// Funciones de resumen y agregados de engagement

import { supabase } from '../../../supabaseClient';
import { getInviteEngagementEvents } from './engagementEvents';
import {
  calculateInterestScore,
  calculateUniqueDays,
  calculateUniqueVisits,
  determineConversionStatus
} from './engagementScoring';

/**
 * Tipos de eventos que cuentan como visita
 * page_view = carga inicial de página
 * page_return = usuario vuelve a la página (retorno)
 */
const VISIT_EVENT_TYPES = ['page_view', 'page_return'];

/**
 * Construir summary completo de un invitado (client-side)
 * ACTUALIZADO: Incluye nuevos campos interestScore, conversionStatus, uniqueDays
 * 
 * @param {string} propertyId - ID de la propiedad
 * @param {string} inviteToken - Token del invitado
 * @returns {Object} Resumen completo del invitado
 */
export const buildSummaryClientSide = async (propertyId, inviteToken, buyerEmail = null) => {
  try {
    const events = await getInviteEngagementEvents(propertyId, inviteToken);

    // Métricas básicas (visitas únicas agrupadas por ventana de 5 min)
    const visits = calculateUniqueVisits(events, 5);
    // Tiempo total DEPRECADO (no confiable sin control de visibilidad)
    const totalTime = 0;
    const lastVisit = events.length > 0 ? events[0].created_at : null;
    
    // Obtener pujas del comprador
    // Prioridad: si hay bids (migradas a subasta abierta), esas mandan.
    // Si no, mira blind_offers (fase ciega). Evita duplicidad al migrar.
    let hasBid = false;
    let bidsCount = 0;
    let maxBidAmount = null;
    let maxBidTime = null;

    if (buyerEmail) {
      const { data: bids } = await supabase
        .from('bids')
        .select('id, amount, bid_time')
        .eq('property_id', propertyId)
        .eq('buyer_email', buyerEmail);

      const openBids = bids || [];

      if (openBids.length > 0) {
        // Ya migró a bids (subasta abierta/last chance) → fuente de verdad
        bidsCount = openBids.length;
        const topBid = openBids.reduce((best, b) =>
          b.amount > best.amount || (b.amount === best.amount && b.bid_time < best.bid_time) ? b : best
        );
        maxBidAmount = topBid.amount;
        maxBidTime = topBid.bid_time;
      } else {
        // Sin bids → mirar ofertas ciegas verificadas
        const { data: blindOffers } = await supabase
          .from('blind_offers')
          .select('id, amount, created_at')
          .eq('property_id', propertyId)
          .eq('buyer_email', buyerEmail)
          .eq('is_verified', true);

        const blindBids = blindOffers || [];
        bidsCount = blindBids.length;

        if (blindBids.length > 0) {
          const topBid = blindBids.reduce((best, b) =>
            b.amount > best.amount || (b.amount === best.amount && b.created_at < best.created_at) ? b : best
          );
          maxBidAmount = topBid.amount;
          maxBidTime = topBid.created_at;
        }
      }

      hasBid = bidsCount > 0;
    }
    
    // Calcular métricas de scoring (v2: usa bidCount y conversionStatus)
    const uniqueDays = calculateUniqueDays(events);
    const conversionStatus = determineConversionStatus(events, hasBid);
    const interestScore = calculateInterestScore(events, bidsCount, conversionStatus);
    
    // Intentos de puja (excluye éxitos, solo cuenta interacciones sin completar)
    const biddingAttempts = events.filter(e => 
      e.event_type === 'bid_submit_attempt_failed' || 
      e.event_type === 'bid_form_focus' ||
      e.event_type === 'bid_confirm_modal_opened' ||
      e.event_type === 'bid_confirm_modal_abandoned'
    ).length;

    // ¿Llegó a intentar enviar? (clic en botón, no solo explorar)
    const hasSubmitAttempt = events.some(e => 
      e.event_type === 'bid_submit_attempt_failed' ||
      e.event_type === 'bid_confirm_modal_opened' ||
      e.event_type === 'bid_confirm_modal_abandoned'
    );
    
    // LEGACY: isStuck se calcula diferente ahora
    // Antes: score >= 50 && !hasBid && visits >= 2
    // Ahora: conversionStatus === 'attempted' (intentó pero no completó)
    const isStuck = conversionStatus === 'attempted';
    
    return {
      // Nuevos campos (scoring v2)
      interestScore,
      conversionStatus,
      uniqueDays,
      biddingAttempts,
      hasSubmitAttempt,

      // Datos de pujas
      bidsCount,
      maxBidAmount,
      maxBidTime,

      // Campos existentes (mantener compatibilidad)
      score: interestScore, // Legacy: apunta al nuevo interestScore
      visits,
      totalTime,
      lastVisit,
      hasBid,
      isStuck,
      eventCount: events.length,
    };
  } catch (error) {
    console.error('Error construyendo summary:', error);
    return {
      interestScore: 0,
      conversionStatus: 'watching',
      uniqueDays: 0,
      biddingAttempts: 0,
      hasSubmitAttempt: false,
      bidsCount: 0,
      maxBidAmount: null,
      maxBidTime: null,
      score: 0,
      visits: 0,
      totalTime: 0,
      lastVisit: null,
      hasBid: false,
      isStuck: false,
      eventCount: 0,
    };
  }
};

/**
 * Obtener resumen completo de engagement de un invitado
 * Intenta RPC primero, fallback a client-side
 */
export const getInviteEngagementSummary = async (propertyId, inviteToken) => {
  try {
    const { data, error } = await supabase
      .rpc('get_invite_engagement_summary', {
        p_property_id: propertyId,
        p_invite_token: inviteToken,
      });

    if (error) {
      // Si la función RPC no existe, construir en client
      if (error.code === '42883') {
        return await buildSummaryClientSide(propertyId, inviteToken);
      }
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error obteniendo resumen de engagement:', error);
    return await buildSummaryClientSide(propertyId, inviteToken);
  }
};

/**
 * Obtener resumen de engagement de toda una propiedad
 * Itera sobre todos los invitados y calcula su engagement
 */
export const getPropertyEngagementSummary = async (propertyId) => {
  try {
    // Obtener todos los invitados
    const { data: invites, error: invitesError } = await supabase
      .from('invites')
      .select('email, name, phone, token')
      .eq('property_id', propertyId);

    if (invitesError) throw invitesError;

    if (!invites || invites.length === 0) {
      console.log('📭 No hay invitados para esta propiedad');
      return [];
    }

    console.log(`📊 Calculando engagement para ${invites.length} invitados...`);

    // Calcular engagement para cada invitado
    const summaries = await Promise.all(
      invites.map(async (invite) => {
        try {
          const summary = await buildSummaryClientSide(propertyId, invite.token, invite.email);
          return {
            email: invite.email,
            name: invite.name,
            phone: invite.phone,
            token: invite.token,
            ...summary,
          };
        } catch (error) {
          console.error(`Error en engagement de ${invite.email}:`, error);
          return {
            email: invite.email,
            name: invite.name,
            phone: invite.phone,
            token: invite.token,
            interestScore: 0,
            conversionStatus: 'watching',
            uniqueDays: 0,
            biddingAttempts: 0,
            hasSubmitAttempt: false,
            bidsCount: 0,
            maxBidAmount: null,
            maxBidTime: null,
            score: 0,
            visits: 0,
            totalTime: 0,
            lastVisit: null,
            hasBid: false,
            isStuck: false,
            eventCount: 0,
          };
        }
      })
    );

    // Ordenar por interestScore descendente
    const sorted = summaries.sort((a, b) => b.interestScore - a.interestScore);
    
    console.log(`✅ Engagement calculado para ${sorted.length} invitados`);
    return sorted;
    
  } catch (error) {
    console.error('❌ Error obteniendo resumen de propiedad:', error);
    throw error;
  }
};

/**
 * Obtener resumen de engagement + perfiles de compradores
 * Combina datos de engagement con buyer_profiles
 */
export const getPropertyEngagementWithProfiles = async (propertyId) => {
  try {
    // 1. Obtener engagement básico
    const summaries = await getPropertyEngagementSummary(propertyId);
    
    // 2. Obtener todos los perfiles de esta propiedad
    const { data: profiles, error: profilesError } = await supabase
      .from('buyer_profiles')
      .select('*')
      .eq('property_id', propertyId);

    if (profilesError) throw profilesError;

    // 3. Mapear perfiles por invite_token
    const profilesByToken = {};
    if (profiles) {
      profiles.forEach(p => {
        profilesByToken[p.invite_token] = {
          id: p.id,
          paymentMethod: p.payment_method,
          financingStatus: p.financing_status,
          propertyToSell: p.property_to_sell,
          minBedrooms: p.min_bedrooms,
          minBathrooms: p.min_bathrooms,
          desiredSqm: p.desired_sqm,
          outdoorSpace: p.outdoor_space,
          reformLevel: p.reform_level,
          elevator: p.elevator,
          parking: p.parking,
          additionalComments: p.additional_comments,
          fitPercentage: p.fit_percentage, // % de compatibilidad con la propiedad
          createdAt: p.created_at,
          updatedAt: p.updated_at,
        };
      });
    }

    // 4. Combinar engagement + perfil
    const enrichedSummaries = summaries.map(summary => ({
      ...summary,
      profile: profilesByToken[summary.token] || null,
    }));

    console.log(`✅ Engagement + Perfiles: ${enrichedSummaries.length} compradores (${profiles?.length || 0} con perfil)`);
    return enrichedSummaries;
    
  } catch (error) {
    console.error('❌ Error obteniendo engagement con perfiles:', error);
    throw error;
  }
};

/**
 * Obtener estadísticas agregadas de engagement de una propiedad
 */
export const getEngagementStats = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('engagement_events')
      .select('event_type, created_at, session_id')
      .eq('property_id', propertyId)
      .limit(10000);

    if (error) throw error;

    // Calcular métricas básicas
    const uniqueSessions = new Set(data.map(e => e.session_id)).size;
    const totalEvents = data.length;
    const eventTypesCounts = {};

    data.forEach(event => {
      eventTypesCounts[event.event_type] = (eventTypesCounts[event.event_type] || 0) + 1;
    });

    return {
      uniqueSessions,
      totalEvents,
      eventTypesCounts,
    };
  } catch (error) {
    console.error('Error obteniendo estadísticas:', error);
    throw error;
  }
};
// =============================================
// FUNCIÓN A AÑADIR AL FINAL DE engagementSummary.js
// (después de getEngagementStats)
// =============================================

/**
 * Obtener todos los intentos de puja de una propiedad
 * Agrupa eventos de bidding por persona y procesa en intentos
 * Devuelve TODOS los intentos (no filtra los exitosos)
 * 
 * @param {string} propertyId - ID de la propiedad
 * @returns {Array} - Array de personas con sus intentos
 */
export const getPropertyBiddingAttempts = async (propertyId) => {
  try {
    // 1. Obtener todos los eventos de bidding de la propiedad
    const { data: events, error: eventsError } = await supabase
      .from('engagement_events')
      .select('*')
      .eq('property_id', propertyId)
      .in('event_type', [
        'bid_form_focus',
        'bid_amount_changed',
        'bid_custom_conditions_written',
        'bid_submit_attempt_failed',
        'bid_submit_success',
        'bid_confirm_modal_opened',
        'bid_confirm_modal_abandoned',
        'bid_verification_email_sent',
        'bid_verified',
        'bid_intent_expired',
        'bid_completed' // Legacy
      ])
      .order('created_at', { ascending: true });

    if (eventsError) throw eventsError;
    if (!events || events.length === 0) return [];

    // 2. Obtener info de invitados para enriquecer con nombre/email
    const { data: invites, error: invitesError } = await supabase
      .from('invites')
      .select('token, email, name')
      .eq('property_id', propertyId);

    if (invitesError) throw invitesError;

    // Mapear invitados por token
    const invitesByToken = {};
    if (invites) {
      invites.forEach(inv => {
        invitesByToken[inv.token] = { email: inv.email, name: inv.name };
      });
    }

    // 3. Agrupar eventos por invite_token
    const eventsByToken = {};
    events.forEach(event => {
      const token = event.invite_token;
      if (!token) return;
      
      if (!eventsByToken[token]) {
        eventsByToken[token] = [];
      }
      eventsByToken[token].push(event);
    });

    // 4. Importar función de agrupación
    const { groupBiddingAttempts } = await import('../../../utils/biddingAttemptsUtils');

    // 5. Procesar cada persona
    const results = [];

    for (const [token, personEvents] of Object.entries(eventsByToken)) {
      const inviteInfo = invitesByToken[token] || { email: token, name: null };
      
      // Agrupar en intentos (TODOS, sin filtrar)
      const allAttempts = groupBiddingAttempts(personEvents);
      
      if (allAttempts.length === 0) continue;

      // Calcular stats
      const allAmounts = allAttempts
        .flatMap(a => [...(a.amounts || []), a.finalAmount])
        .filter(Boolean);
      
      const maxAttemptedAmount = allAmounts.length > 0 
        ? Math.max(...allAmounts) 
        : null;

      const lastAttemptAt = allAttempts
        .map(a => a.startedAt)
        .sort((a, b) => new Date(b) - new Date(a))[0];

      results.push({
        token,
        email: inviteInfo.email,
        name: inviteInfo.name,
        propertyId,
        attempts: allAttempts.sort((a, b) => 
          new Date(a.startedAt) - new Date(b.startedAt)
        ),
        totalAttempts: allAttempts.length,
        lastAttemptAt,
        maxAttemptedAmount,
      });
    }

    // 6. Ordenar por último intento (más reciente primero)
    results.sort((a, b) => new Date(b.lastAttemptAt) - new Date(a.lastAttemptAt));

    console.log(`🔥 Intentos de puja: ${results.length} personas con actividad`);
    return results;

  } catch (error) {
    console.error('❌ Error obteniendo intentos de puja:', error);
    return [];
  }
};